import React, { useEffect, useRef } from 'react';
import { Prompt } from 'react-router';
import { useSelector } from 'react-redux';
import { getNavigationState } from '../features/navigation/selectors';

export default function GlobalNavigationBlocker() {
  const shouldBlock = useSelector(getNavigationState);

  // After a confirmed navigation, we stop prompting again until shouldBlock resets to false.
  const unblockedRef = useRef(false);

  // Keep a ref to the beforeunload handler so we can reliably remove it.
  const beforeUnloadHandlerRef = useRef<((e: BeforeUnloadEvent) => void) | null>(null);

  // If the global flag turns off, allow prompts again on the next time it turns on.
  useEffect(() => {
    if (!shouldBlock) {
      unblockedRef.current = false;
    }
  }, [shouldBlock]);

  const blockActive = !!shouldBlock && !unblockedRef.current;

  // Sync native "beforeunload" only when blocking is active.
  useEffect(() => {
    // Always clean up any stale handler first
    if (beforeUnloadHandlerRef.current) {
      window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
      beforeUnloadHandlerRef.current = null;
    }
    if (!blockActive) {
      window.onbeforeunload = null;
      return;
    }

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    beforeUnloadHandlerRef.current = handler;
    window.addEventListener('beforeunload', handler);

    return () => {
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
      window.onbeforeunload = null;
    };
  }, [blockActive]);

  const handlePrompt = () => {
    // Drive the confirm ourselves so we can run side effects on OK.
    const proceed = window.confirm('Are you sure you want to leave?');
    if (proceed) {
      // Stop further prompts for this session (until shouldBlock becomes false again)
      unblockedRef.current = true;

      // Clear native beforeunload immediately
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current);
        beforeUnloadHandlerRef.current = null;
      }
      window.onbeforeunload = null;
    }
    return proceed;
  };

  return <Prompt when={blockActive} message={handlePrompt} />;
}
